import React, {useState} from "react";
import {Dialog, DialogActions, DialogContent, DialogTitle, Grid2, Typography} from "@mui/material";
import {CloseOutlined} from "@mui/icons-material";
import {useTranslation} from "react-i18next";
import {OpenRequestFeedbackDTO} from "@/api/data-contracts";
import {ButtonEx} from "@/lib/components/buttons/ButtonEx";
import {IconButtonEx} from "@/lib/components/buttons/IconButtonEx";
import ConfirmationDialog from "@/lib/components/ConfirmationDialog";
import FeedbackEntryBlock from "@/pages/login-page/feedback/FeedbackEntryBlock";
import {
    areAllEntriesAnswered,
    createInitialFeedbackEntry,
    FeedbackEntryState,
    fillUnansweredWithNoReason
} from "@/pages/login-page/feedback/feedback-dialog-logic";
import {GeneralI18N, PostLoginFeedbackDialogI18N} from "@/translations";

/**
 * Props for PostLoginFeedbackDialog — a pure presentation component. It never fetches or submits
 * itself; it only reports the collected entry states upward via onConfirm. The caller
 * (FeedbackDialogProvider) owns the API call and decides when to unmount this dialog.
 */
interface PostLoginFeedbackDialogProps {
    feedbacks: OpenRequestFeedbackDTO[];
    onConfirm: (entries: FeedbackEntryState[]) => void;
}

/**
 * Blocking post-login feedback dialog: asks the client why each still-open (PENDING) request of their
 * company was not awarded, rendering one delimited {@link FeedbackEntryBlock} per marker stacked
 * vertically with a single shared Confirm button. Built DIRECTLY on MUI Dialog (not ConfirmationDialog,
 * which supports neither disableEscapeKeyDown nor an onClose(reason) guard) so that only "Bestätigen" or
 * the secondary no-reason confirmation ("x") can close it — backdrop click and Escape are no-ops.
 *
 * The single Confirm button is enabled only once EVERY entry is validly answered
 * ({@link areAllEntriesAnswered}); the global "x" opens a safety prompt and, once confirmed, submits
 * the whole stack with every not-yet-answered entry defaulted to NO_REASON_GIVEN
 * ({@link fillUnansweredWithNoReason}).
 */
const PostLoginFeedbackDialog: React.FC<PostLoginFeedbackDialogProps> = ({feedbacks, onConfirm}) => {
    const {t} = useTranslation();
    const [entries, setEntries] = useState<FeedbackEntryState[]>(() =>
        feedbacks
            .filter((fb): fb is OpenRequestFeedbackDTO & {feedbackId: number} => fb.feedbackId !== undefined)
            .map((fb) => createInitialFeedbackEntry(fb.feedbackId))
    );
    const [noReasonConfirmOpen, setNoReasonConfirmOpen] = useState(false);

    const updateEntry = (feedbackId: number, patch: Partial<FeedbackEntryState>): void =>
        setEntries((prev) => prev.map((entry) => (entry.feedbackId === feedbackId ? {...entry, ...patch} : entry)));

    const confirmEnabled = areAllEntriesAnswered(entries);

    return (
        <>
            <Dialog
                open
                disableEscapeKeyDown
                onClose={(_event, closeReason) => {
                    // Blocking dialog: backdrop click and Escape must never close it — only
                    // "Bestätigen" or the secondary no-reason confirmation ("x") do.
                    if (closeReason === "backdropClick" || closeReason === "escapeKeyDown") {
                        return;
                    }
                }}
                maxWidth="sm"
                fullWidth
                data-cy="post-login-feedback-dialog"
            >
                <DialogTitle>
                    <Grid2 container direction="row" alignItems="center">
                        <Grid2 flexGrow={1}>{t(PostLoginFeedbackDialogI18N.titleQuestionStacked)}</Grid2>
                        <Grid2>
                            <IconButtonEx
                                Icon={CloseOutlined}
                                title={t(PostLoginFeedbackDialogI18N.closeButtonAriaLabel)}
                                onClick={() => setNoReasonConfirmOpen(true)}
                                data-cy="post-login-feedback-close-button"
                            />
                        </Grid2>
                    </Grid2>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                        {t(PostLoginFeedbackDialogI18N.subtitle)}
                    </Typography>
                    {entries.map((entry) => {
                        const feedback = feedbacks.find((fb) => fb.feedbackId === entry.feedbackId);
                        if (feedback === undefined) {
                            return null;
                        }
                        return (
                            <FeedbackEntryBlock
                                key={entry.feedbackId}
                                feedback={feedback}
                                reason={entry.reason}
                                freeText={entry.freeText}
                                shareWithBidder={entry.shareWithBidder}
                                onReasonChange={(reason) => updateEntry(entry.feedbackId, {reason})}
                                onFreeTextChange={(freeText) => updateEntry(entry.feedbackId, {freeText})}
                                onShareWithBidderChange={(shareWithBidder) =>
                                    updateEntry(entry.feedbackId, {shareWithBidder})
                                }
                            />
                        );
                    })}
                </DialogContent>
                <DialogActions sx={{display: "flex", justifyContent: "flex-end", padding: 2}}>
                    <ButtonEx
                        label={t(PostLoginFeedbackDialogI18N.confirmButton)}
                        color="warning"
                        disabled={!confirmEnabled}
                        onClick={() => onConfirm(entries)}
                        data-cy="post-login-feedback-confirm-button"
                    />
                </DialogActions>
            </Dialog>
            <ConfirmationDialog
                open={noReasonConfirmOpen}
                onClose={() => setNoReasonConfirmOpen(false)}
                onConfirm={() => {
                    setNoReasonConfirmOpen(false);
                    onConfirm(fillUnansweredWithNoReason(entries));
                }}
                title={t(PostLoginFeedbackDialogI18N.noReasonConfirmText)}
                message=""
                confirmButtonText={t(GeneralI18N.yes)}
                cancelButtonText={t(GeneralI18N.no)}
                dataCy="post-login-feedback-no-reason-confirm-dialog"
            />
        </>
    );
};

export default PostLoginFeedbackDialog;
